/* import __COLOCATED_TEMPLATE__ from './conversation-initiator-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'ember-intl/services/intl';
import { type SelectedFilter } from 'embercom/services/filter-pattern-service';

interface Args {
  icon: string;
  name: string;
  initiallyOpened: boolean;
  removeFilter: () => void;
  setSelected: (filterValues: string[], operator: string) => void;
  selected: SelectedFilter;
  type: string;
  analyticsObject: any;
  isDisabled: boolean;
}

interface Item {
  text: string;
  value: string;
  isSelected: boolean;
}

// Maps ES string values to translation keys
const INITIATOR_TYPE_KEYS: Record<string, string> = {
  '49': 'chat',
  '50': 'post',
  '57': 'email',
  '67': 'message',
  '77': 'workflow',
  '102': 'sms',
  '117': 'whatsapp',
  '119': 'article',
  '125': 'ticket',
  '154': 'phone-call',
};

export default class ConversationInitiatorFilter extends Component<Args> {
  @service declare intercomEventService: any;
  @service declare intl: IntlService;

  @tracked selectedDropdownItems: string[] = this.args.selected.values || [];
  @tracked operator = this.args.selected.operator || 'category';

  get initiatorTypeLabels(): Record<string, string> {
    return Object.entries(INITIATOR_TYPE_KEYS).reduce(
      (acc, [value, key]) => {
        acc[value] = this.intl.t(
          `components.reporting.custom.chart-builder.filter-bar.conversation-initiator-filter.${key}`,
        );
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  get selectedTypes(): string[] {
    return this.selectedDropdownItems.filter((item) => item !== REPORTING_FILTER_SELECT_ALL);
  }

  get allItems(): Item[] {
    return Object.entries(this.initiatorTypeLabels).map(([value, text]) => ({
      text,
      value,
      isSelected: this.selectedDropdownItems.includes(value),
    }));
  }

  get currentLabel(): string {
    if (
      this.selectedDropdownItems?.length &&
      this.selectedDropdownItems[0] !== REPORTING_FILTER_SELECT_ALL
    ) {
      return this.selectedDropdownItems.map((value) => this.initiatorTypeLabels[value]).join(', ');
    } else {
      return this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-initiator-filter.any',
      );
    }
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_conversation_initiator',
      object: this.args.analyticsObject,
      types: this.selectedTypes,
      operator: this.operator,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::ConversationInitiatorFilter': typeof ConversationInitiatorFilter;
    'reporting/custom/chart-builder/filter-bar/conversation-initiator-filter': typeof ConversationInitiatorFilter;
  }
}
